import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstileOnLoad&render=explicit";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
      remove: (widgetId: string) => void;
      execute: (widgetId?: string) => void;
    };
    __turnstileOnLoad?: () => void;
    // Set by the server-rendered auth layout so the Turnstile script starts
    // downloading alongside the Vite bundle (instead of after React mounts).
    __turnstilePromise?: Promise<void>;
  }
}

// Module-level promise resolves when the Turnstile script finishes loading.
// Shared across widgets so we only load the script once per page.
let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstilePromise) return window.__turnstilePromise;
  if (scriptLoadPromise) return scriptLoadPromise;

  // Fallback path: no server-rendered script tag. Inject one ourselves.
  scriptLoadPromise = new Promise((resolve, reject) => {
    window.__turnstileOnLoad = () => resolve();

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export interface TurnstileWidgetHandle {
  getToken: () => string | undefined;
  reset: () => void;
  isReady: () => boolean;
}

interface TurnstileWidgetProps {
  siteKey: string;
  onTokenChange?: (token: string | undefined) => void;
  onError?: (error: Error) => void;
  onReady?: (ready: boolean) => void;
}

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ siteKey, onTokenChange, onError, onReady }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [ready, setReady] = useState(false);

    useImperativeHandle(ref, () => ({
      getToken: () => (window.turnstile && widgetIdRef.current)
        ? window.turnstile.getResponse(widgetIdRef.current)
        : undefined,
      reset: () => {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
      isReady: () => ready,
    }), [ready]);

    useEffect(() => {
      let cancelled = false;

      loadTurnstileScript()
        .then(() => {
          if (cancelled || !containerRef.current || !window.turnstile) return;

          const id = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            appearance: "interaction-only",
            size: "invisible",
            callback: (token: string) => {
              onTokenChange?.(token);
              setReady(true);
              onReady?.(true);
            },
            "error-callback": () => {
              onError?.(new Error("Turnstile challenge failed"));
              setReady(false);
              onReady?.(false);
            },
            "expired-callback": () => {
              onTokenChange?.(undefined);
              setReady(false);
              onReady?.(false);
            },
            "timeout-callback": () => {
              onTokenChange?.(undefined);
              setReady(false);
              onReady?.(false);
            },
          });
          widgetIdRef.current = id;
        })
        .catch((err: Error) => {
          if (cancelled) return;
          onError?.(err);
        });

      return () => {
        cancelled = true;
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
      // siteKey is stable per page render; onTokenChange/onError intentionally
      // omitted to avoid re-initializing the widget on every parent render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey]);

    return <div ref={containerRef} data-turnstile-widget />;
  }
);
